import React from "react";
import { Ribbon, Card, Empty, Delta } from "../../components/analytics-kit";
import { ChartLoader } from "../../components/loaders";
import Chart from "../../components/chart";
import { metricLabel, fmtCell, fmtByKey, isMoneyKey, kitKpiDelta, widthSpan } from "./common";

/*
 * One report panel, drawn the same way in the report viewer and on the Dashboard (PlunkDocs
 * specs/MY_DASHBOARD_2026-10-22.md step 2: report charts pinned by reference). Moved out of viewer.jsx unchanged:
 * KPI ribbon, line / bar chart with the earlier period, table with share bars and top 10 + Show all.
 *   <ReportPanel panel={result} registry={registry} pd={periodOf(run, range)} action={node} foot={node} />
 * `pd` describes the dates (periodOf); `action` sits at the right of the heading; `foot` under the body.
 */

const EMPTY_TEXT = "Nothing to show for these dates yet.";
const TABLE_TOP = 10;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SERIES_COLORS = ["#ff6358", "#2f80ed", "#14b8a6", "#9fb3c8"];
const COMPARE_COLOR = "#9fb3c8";
const RANGE_NAMES = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
  last_year: "Last year",
  last_7_days: "Last 7 days",
  last_30_days: "Last 30 days",
  all_time: "All time"
};

// ------------------------------------------------------------------ dates

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;
function parseYmd(s) {
  const m = YMD.exec(String(s || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function addDays(d, n) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
}
function dayDiff(a, b) {
  return Math.round((Date.UTC(a.getFullYear(), a.getMonth(), a.getDate()) - Date.UTC(b.getFullYear(), b.getMonth(), b.getDate())) / 86400000);
}
/** "Aug 18" or "Aug 18, 2026". */
function fmtDay(d, withYear) {
  return d ? `${MONTHS[d.getMonth()]} ${d.getDate()}${withYear ? `, ${d.getFullYear()}` : ""}` : "";
}
/** "Jan 1 to Oct 9, 2026" (both years shown when they differ). */
function fmtSpan(from, to) {
  if (!from || !to) return "";
  if (ymd(from) === ymd(to)) return fmtDay(from, true);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${fmtDay(from, !sameYear)} to ${fmtDay(to, true)}`;
}
function lastOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function isWholeMonth(from, to) {
  return from && to && from.getDate() === 1 && from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear() && to.getDate() === lastOfMonth(to).getDate();
}
function isWholeYear(from, to) {
  return from && to && from.getMonth() === 0 && from.getDate() === 1 && to.getMonth() === 11 && to.getDate() === 31 && from.getFullYear() === to.getFullYear();
}
/** The comparison period, mirroring ReportRunner.PreviousWindow (inclusive ends). */
function previousSpan(from, to, comparison) {
  if (!from || !to) return null;
  if (comparison === "previous_year") {
    return {
      from: new Date(from.getFullYear() - 1, from.getMonth(), from.getDate()),
      to: new Date(to.getFullYear() - 1, to.getMonth(), to.getDate())
    };
  }
  const len = dayDiff(to, from) + 1;
  return {
    from: addDays(from, -len),
    to: addDays(from, -1)
  };
}
/** Short name for the earlier period: "August", "2025", "the same dates last year", "Sep 22 to Sep 30". */
function prevName(cur, prev, comparison) {
  if (!prev) return "the period before";
  if (isWholeMonth(prev.from, prev.to)) {
    return prev.from.getFullYear() === cur.from.getFullYear() ? MONTHS_LONG[prev.from.getMonth()] : `${MONTHS_LONG[prev.from.getMonth()]} ${prev.from.getFullYear()}`;
  }
  if (isWholeYear(prev.from, prev.to)) return String(prev.from.getFullYear());
  if (comparison === "previous_year") return "the same dates last year";
  return fmtSpan(prev.from, prev.to);
}
function isDateStr(v) {
  return YMD.test(String(v == null ? "" : v));
}

// ------------------------------------------------------------- panel bits

const num = v => {
  const n = Number(v);
  return isFinite(n) ? n : 0;
};

/** True when a panel's earlier-period rows carry any non-zero number. */
function prevHasData(p) {
  const first = p.kind === "kpi" ? 0 : 1;
  return (p.previousRows || []).some(r => (r || []).some((v, ci) => ci >= first && v != null && num(v) !== 0));
}
const RANGE_PHRASES = [["today", /\btoday\b/i], ["yesterday", /\byesterday\b/i], ["this_week", /\bthis week\b/i], ["last_week", /\blast week\b/i], ["this_month", /\bthis month\b/i], ["last_month", /\blast month\b/i], ["this_quarter", /\bthis quarter\b/i], ["last_quarter", /\blast quarter\b/i], ["this_year", /\bthis year\b/i], ["last_year", /\blast year\b/i], ["last_7_days", /\blast 7 days\b/i], ["last_30_days", /\blast 30 days\b/i]];
/** The builder's auto-title ("Products · Table", older "Products — Table"). */
const AUTO_TITLE = /\s[·—-]\s(KPI strip|Line|Bar|Table)$/;
function isAutoTitle(t) {
  return AUTO_TITLE.test(String(t || ""));
}
/** Builder auto-titles read as the dataset name alone. */
function cleanTitle(t) {
  return String(t || "").replace(AUTO_TITLE, "");
}
/** A KPI title worth showing: not auto, not a bare range phrase, not a range other than the one on screen. */
function kpiTitle(title, range) {
  const t = String(title || "").trim();
  if (!t || isAutoTitle(t)) return null;
  if (RANGE_PHRASES.some(([, re]) => re.test(t) && t.replace(re, "").trim() === "")) return null;
  if (RANGE_PHRASES.some(([tok, re]) => tok !== range && re.test(t))) return null;
  return t;
}
/** True when a title names a month ("September KPIs") that the dates on screen do not cover. */
function namesOtherMonth(title, from, to) {
  const t = String(title || "");
  if (!from || !to) return false;
  return MONTHS_LONG.some((m, mi) => {
    if (!new RegExp(`\\b${m}\\b`, "i").test(t)) return false;
    for (let d = new Date(from.getFullYear(), from.getMonth(), 1); d <= to; d = new Date(d.getFullYear(), d.getMonth() + 1, 1)) {
      if (d.getMonth() === mi) return false;
    }
    return true;
  });
}

/**
 * Pack saved widths (6 units: full 6 / half 3 / third 2) into rows in
 * order; a row holding one non-full panel renders it full width.
 */
function packSpans(widths) {
  const spans = widths.map(w => widthSpan(w));
  const rows = [];
  let row = [],
    used = 0;
  spans.forEach((s, i) => {
    if (used + s > 6 && row.length) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(i);
    used += s;
  });
  if (row.length) rows.push(row);
  const out = spans.slice();
  rows.forEach(r => {
    if (r.length === 1) out[r[0]] = 6;
  });
  return out;
}
export { cleanTitle, packSpans, MONTHS, MONTHS_LONG, RANGE_NAMES, EMPTY_TEXT, parseYmd, fmtDay, fmtSpan, isWholeMonth, isWholeYear, previousSpan, prevName, prevHasData };

/** Everything a panel needs to talk about the dates, from a run ({range, window, comparison, panels}). */
export function periodOf(run, rangeFallback) {
  const range = String(run && run.range || rangeFallback || "");
  const w = run && run.window;
  // JSONbig parses to null-prototype objects: read the fields, never interpolate run.window itself.
  const from = parseYmd(w && w.from),
    to = parseYmd(w && w.to);
  const comparison = run && run.comparison && run.comparison !== "none" ? String(run.comparison) : null;
  const prev = comparison ? previousSpan(from, to, comparison) : null;
  const prevShort = comparison ? prevName({
    from,
    to
  }, prev, comparison) : null;
  const panels = run && run.panels || [];
  const compareOk = !!comparison && panels.some(prevHasData);
  return {
    range,
    from,
    to,
    comparison,
    prev,
    prevShort,
    compareOk
  };
}
function renderPanel(p, registry, pd, ctx) {
  let title = p.kind === "kpi" ? kpiTitle(p.title, pd.range) : cleanTitle(p.title);
  // A panel titled for one month ("September KPIs") while the top bar shows another: drop the
  // stale month from the heading rather than label October numbers as September.
  if (title && namesOtherMonth(title, pd.from, pd.to)) title = p.kind === "kpi" ? null : metricLabel(registry, p.dataset, (p.metrics || [])[0]) || "Results";
  if (p.error) {
    return /*#__PURE__*/React.createElement(Card, {
      title: title || cleanTitle(p.title),
      right: ctx.action
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger",
      style: {
        margin: 0
      }
    }, p.error), ctx.foot);
  }
  const keys = p.columns || [];
  const cols = keys.map(c => metricLabel(registry, p.dataset, c));
  const rows = p.rows || [];
  const kpiEmpty = p.kind === "kpi" && rows.length && (rows[0] || []).every(v => v == null);
  if (!rows.length || kpiEmpty) {
    return /*#__PURE__*/React.createElement(Card, {
      title: title || cleanTitle(p.title),
      right: ctx.action
    }, /*#__PURE__*/React.createElement(Empty, null, EMPTY_TEXT), ctx.foot);
  }
  if (p.kind === "kpi") return renderKpi(p, title, cols, keys, rows, pd, ctx);
  if (p.kind === "line" || p.kind === "bar") return renderChart(p, title, cols, keys, rows, pd, ctx);
  return renderTable(p, title, cols, keys, rows, pd, ctx);
}
function renderKpi(p, title, cols, keys, rows, pd, ctx) {
  const now = rows[0] || [];
  const prevRow = pd.compareOk && prevHasData(p) ? (p.previousRows || [])[0] || null : null;
  const tiles = cols.map((label, ci) => ({
    l: label,
    v: fmtByKey(keys[ci], now[ci]),
    d: prevRow ? kitKpiDelta(now[ci], prevRow, ci) : null,
    s: prevRow ? `vs ${pd.prevShort}` : ""
  }));
  return /*#__PURE__*/React.createElement("div", {
    className: "rv-kpi"
  }, title || ctx.action ? /*#__PURE__*/React.createElement("div", {
    className: "rv-label"
  }, title, ctx.action ? /*#__PURE__*/React.createElement("span", {
    className: "rv-label-act"
  }, ctx.action) : null) : null, /*#__PURE__*/React.createElement(Ribbon, {
    comparing: prevRow ? true : null,
    cols: tiles.length === 3 ? 3 : undefined,
    tiles: tiles
  }), ctx.foot);
}
function renderChart(p, title, cols, keys, rows, pd, ctx) {
  // Today's partial day draws as a cliff on date charts: leave it out and say so.
  const today = ymd(new Date());
  const isDateDim = isDateStr((rows[0] || [])[0]);
  const chartRows = isDateDim ? rows.filter(r => String(r[0]) !== today) : rows;
  const droppedToday = isDateDim && chartRows.length !== rows.length;
  if (!chartRows.length) {
    return /*#__PURE__*/React.createElement(Card, {
      title: title,
      right: ctx.action
    }, /*#__PURE__*/React.createElement(Empty, null, EMPTY_TEXT, " Today is left out until the day is over."), ctx.foot);
  }

  // Earlier-period rows, aligned to the current rows (by shifted date on date charts, else by position).
  const prevRows = pd.compareOk && prevHasData(p) ? p.previousRows || [] : [];
  let aligned = null;
  if (prevRows.length) {
    if (isDateDim && pd.from && pd.prev) {
      const byDate = {};
      const shift = pd.comparison === "previous_year" ? null : dayDiff(pd.from, pd.prev.from);
      prevRows.forEach(r => {
        const d = parseYmd(r[0]);
        if (!d) return;
        const k = shift == null ? ymd(new Date(d.getFullYear() + 1, d.getMonth(), d.getDate())) : ymd(addDays(d, shift));
        byDate[k] = r;
      });
      aligned = chartRows.map(r => byDate[String(r[0])] || null);
    } else if (isDateDim) {
      aligned = chartRows.map((r, ri) => prevRows[ri] || null);
    } else {
      const byKey = {};
      prevRows.forEach(r => {
        byKey[String(r[0])] = r;
      });
      aligned = chartRows.map(r => byKey[String(r[0])] || null);
    }
    if (!aligned.some(Boolean)) aligned = null;
  }
  const seriesNames = cols.slice(1);
  const single = seriesNames.length === 1;
  const prevLabel = pd.prevShort ? pd.prevShort.charAt(0).toUpperCase() + pd.prevShort.slice(1) : "";
  const header = [cols[0], ...seriesNames];
  if (aligned) seriesNames.forEach(n => header.push(single ? prevLabel : `${n} (${prevLabel})`));
  const data = [header];
  chartRows.forEach((r, ri) => {
    const label = isDateDim ? fmtDay(parseYmd(r[0])) : String(r[0] == null ? "" : r[0]);
    const vals = r.slice(1).map(v => v == null ? null : num(v));
    const pv = aligned ? seriesNames.map((n, si) => {
      const pr = aligned[ri];
      return pr && pr[si + 1] != null ? num(pr[si + 1]) : null;
    }) : [];
    data.push([label, ...vals, ...pv]);
  });
  const series = {};
  seriesNames.forEach((n, si) => {
    series[si] = {
      color: SERIES_COLORS[si % SERIES_COLORS.length]
    };
    if (aligned) series[seriesNames.length + si] = {
      color: single ? COMPARE_COLOR : SERIES_COLORS[si % SERIES_COLORS.length],
      ...(p.kind === "line" ? {
        lineDashStyle: [4, 3]
      } : {})
    };
  });
  if (aligned && p.kind === "bar") seriesNames.forEach((n, si) => {
    series[seriesNames.length + si].color = single ? COMPARE_COLOR : "#c9d4df";
  });
  const money = keys.slice(1).some(k => isMoneyKey(k));
  // Money next to counts (revenue and orders): counts get their own right-hand axis, else
  // orders (tens) draw flat at 0 on a revenue scale (thousands).
  const mixed = money && keys.slice(1).some(k => !isMoneyKey(k));
  if (mixed) {
    seriesNames.forEach((n, si) => {
      const axis = isMoneyKey(keys[si + 1]) ? 0 : 1;
      series[si].targetAxisIndex = axis;
      if (aligned) series[seriesNames.length + si].targetAxisIndex = axis;
    });
  }
  const n = chartRows.length;
  const hint = [aligned && p.kind === "line" ? `dashed line is ${pd.prevShort}` : null, droppedToday ? "Today is left out until the day is over" : null].filter(Boolean).join(" · ");
  return /*#__PURE__*/React.createElement(Card, {
    title: title,
    hint: hint || null,
    right: ctx.action
  }, /*#__PURE__*/React.createElement("div", {
    className: "rv-chart"
  }, /*#__PURE__*/React.createElement(Chart, {
    width: "100%",
    height: "260px",
    chartType: p.kind === "line" ? "LineChart" : "ColumnChart",
    loader: /*#__PURE__*/React.createElement(ChartLoader, null),
    data: data,
    options: {
      legend: {
        position: header.length > 2 ? "top" : "none"
      },
      series,
      ...(mixed ? {
        vAxes: {
          0: {
            format: "short"
          },
          1: {
            format: "short",
            gridlines: {
              color: "transparent"
            }
          }
        }
      } : {
        vAxis: {
          format: "short"
        }
      }),
      hAxis: {
        showTextEvery: Math.max(1, Math.ceil(n / 8)),
        slantedText: false
      },
      pointSize: n > 45 ? 0 : 3,
      tooltip: {
        format: money ? "currency" : "{0:n0}"
      }
    }
  })), ctx.foot);
}
function renderTable(p, title, cols, keys, rows, pd, ctx) {
  // §280.7b comparison deltas, rows matched on the first column.
  const prevMap = {};
  if (pd.compareOk) (p.previousRows || []).forEach(r => {
    prevMap[String(r[0])] = r;
  });
  const hasPrev = Object.keys(prevMap).length > 0;
  const firstIsText = rows.some(r => typeof r[0] === "string" && r[0] !== "" && !isFinite(Number(r[0])));
  const shareCol = firstIsText && cols.length >= 2 && rows.every(r => r[1] == null || isFinite(Number(r[1]))) ? 1 : -1;
  const max = shareCol > 0 ? Math.max(0, ...rows.map(r => num(r[shareCol]))) : 0;
  const showShare = shareCol > 0 && max > 0;
  const all = !!ctx.all;
  const shown = all ? rows : rows.slice(0, TABLE_TOP);
  const hintParts = [rows.length > TABLE_TOP ? `top ${Math.min(TABLE_TOP, rows.length)} of ${rows.length}` : null, hasPrev ? `change vs ${pd.prevShort}` : null].filter(Boolean);
  const firstCell = v => isDateStr(v) ? fmtDay(parseYmd(v), true) : fmtCell(v);
  return /*#__PURE__*/React.createElement(Card, {
    title: title,
    hint: hintParts.join(" · ") || null,
    right: ctx.action
  }, /*#__PURE__*/React.createElement("div", {
    className: "tblwrap rv-tbl"
  }, /*#__PURE__*/React.createElement("table", {
    className: "territory-table wa-table"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, cols[0]), showShare ? /*#__PURE__*/React.createElement("th", {
    className: "rv-share-h"
  }, "Share of ", String(cols[shareCol]).toLowerCase()) : null, cols.slice(1).map((c, ci) => /*#__PURE__*/React.createElement("th", {
    key: ci,
    className: "n"
  }, c)))), /*#__PURE__*/React.createElement("tbody", null, shown.map((r, ri) => {
    const pr = hasPrev ? prevMap[String(r[0])] : null;
    return /*#__PURE__*/React.createElement("tr", {
      key: ri
    }, /*#__PURE__*/React.createElement("td", {
      className: "rv-first"
    }, firstCell(r[0])), showShare ? /*#__PURE__*/React.createElement("td", {
      className: "rv-share"
    }, /*#__PURE__*/React.createElement("span", {
      className: "rv-bar"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${Math.max(1, Math.round(num(r[shareCol]) / max * 100))}%`
      }
    }))) : null, r.slice(1).map((v, k) => {
      const ci = k + 1;
      let delta = null;
      if (pr && v != null && isFinite(Number(v))) {
        const pv = num(pr[ci]);
        if (pv > 0) {
          const d = (num(v) - pv) / pv * 100;
          delta = /*#__PURE__*/React.createElement("span", {
            className: "rv-delta"
          }, /*#__PURE__*/React.createElement(Delta, {
            d: {
              dir: d > 0 ? "up" : d < 0 ? "down" : "flat",
              pct: d
            }
          }));
        }
      }
      return /*#__PURE__*/React.createElement("td", {
        key: ci,
        className: "n"
      }, fmtByKey(keys[ci], v), delta);
    }));
  })))), rows.length > TABLE_TOP ? /*#__PURE__*/React.createElement("div", {
    className: "rv-more"
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "rv-link",
    onClick: ctx.toggle
  }, all ? `Show top ${TABLE_TOP}` : `Show all ${rows.length}`)) : null, ctx.foot);
}
export function ReportPanel({
  panel,
  registry,
  pd,
  action = null,
  foot = null
}) {
  const [all, setAll] = React.useState(false);
  return renderPanel(panel, registry, pd, {
    all,
    toggle: () => setAll(!all),
    action,
    foot
  });
}